<template>
  <div class="traite-section w-100">
    <!-- Header Section -->
    <div class="traite-header">
      <a-divider class="traite-divider">
        <span class="divider-text">{{ $t("recette.saisirInfoTraite") }}</span>
      </a-divider>
    </div>

    <!-- Column Headers -->
    <div class="traite-columns-header">
      <div class="column-header index-col">Indice</div>
      <div class="column-header">Numéro</div>
      <div class="column-header">Date d'échéance</div>
      <div class="column-header">Banque</div>
      <div class="column-header">RIB</div>
      <div class="column-header">Montant</div>
      <div class="column-header actions-col">Actions</div>
    </div>

    <!-- Traite Items -->
    <div class="traite-items">
      <div
        v-for="(item, index) in traitesData"
        :key="index"
        class="traite-item"
        :class="{ 'traite-item--highlighted': index % 2 === 0 }"
      >
        <div class="traite-index">
          <span class="traite-number">{{ index + 1 }}</span>
        </div>

        <div class="traite-field">
          <a-input
            :value="item.num"
            placeholder="Numéro de la traite"
            size="small"
            :class="{ 'input-error': isFieldInvalid(index, 'num') }"
            @input="(e) => handleNumInput(index, e.target.value)"
            @change="(e) => handleTraiteUpdate(index, 'num', e.target.value)"
          />
        </div>

        <div class="traite-field">
          <a-date-picker
            :value="item.dateEchance"
            placeholder="Date"
            size="small"
            style="width: 100%"
            :class="{ 'input-error': isFieldInvalid(index, 'dateEchance') }"
            @change="(date) => handleTraiteUpdate(index, 'dateEchance', date)"
          />
        </div>

        <div class="traite-field">
          <BankSelect
            :value="item.bank"
            placeholder="Nom de la banque"
            size="small"
            @change="(value) => handleTraiteUpdate(index, 'bank', value)"
          />
        </div>

        <div class="traite-field">
          <a-input
            :value="item.rib"
            placeholder="RIB"
            size="small"
            maxlength="20"
            :class="{ 'input-error': isFieldInvalid(index, 'rib') }"
            @input="(e) => handleRibInput(index, e.target.value)"
            @change="(e) => handleTraiteUpdate(index, 'rib', e.target.value)"
          />
        </div>

        <div class="traite-field">
          <a-input-number
            :value="item.montant"
            placeholder="Montant"
            size="small"
            style="width: 100%"
            :formatter="
              (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            "
            :parser="(value) => value.replace(/\$\s?|(,*)/g, '')"
            :class="{ 'input-error': isFieldInvalid(index, 'montant') }"
            @change="(value) => handleTraiteUpdate(index, 'montant', value)"
          />
        </div>

        <div class="traite-actions">
          <a-button
            v-if="index > 0"
            type="text"
            size="small"
            danger
            @click="handleRemoveTraite(index)"
            class="remove-btn"
            icon="delete"
            title="Supprimer ce chèque"
          >
          </a-button>
        </div>
      </div>
    </div>

    <!-- Add Traite Button -->
    <div class="traite-actions-footer">
      <a-button
        type="dashed"
        icon="plus"
        @click="handleAddTraite"
        class="add-traite-btn"
        size="large"
      >
        Ajouter une traite
      </a-button>
    </div>
  </div>
</template>

<script>
import BankSelect from "@/components/BankSelect";

export default {
  name: "TraiteSection",

  components: {
    BankSelect,
  },

  props: {
    traitesData: {
      required: true,
      type: Array,
    },
    traiteVide: {
      required: true,
      type: Object,
    },
  },

  data() {
    return {
      requiredFields: ["num", "dateEchance", "rib", "montant"],
      validationErrors: [],
    };
  },

  methods: {
    handleTraiteUpdate(index, field, value) {
      this.$emit("update-traite", index, field, value);
      if (field === "montant") {
        this.$emit("calculate-montant");
      }
      this.validateTraite(index);
    },

    handleAddTraite() {
      // Validate last traite before adding
      const lastIndex = this.traitesData.length - 1;
      if (!this.validateTraite(lastIndex)) {
        return;
      }
      this.$emit("add-traite");
    },

    handleRemoveTraite(index) {
      this.$emit("remove-traite", index);
      this.validationErrors.splice(index, 1);
    },

    validateTraite(index) {
      const traite = this.traitesData[index];
      if (!traite) return true;
      const errors = {};
      this.requiredFields.forEach((field) => {
        if (field === "montant") {
          if (!traite[field] || traite[field] <= 0) {
            errors[field] = true;
          }
        } else if (field === "rib") {
          if (!traite[field] || traite[field].length === 0) {
            errors[field] = true;
          } else if (traite[field].length !== 20) {
            errors[field] = true;
          } else if (!/^[0-9]+$/.test(traite[field])) {
            errors[field] = true;
          }
        } else if (field === "num") {
          if (!traite[field] || traite[field].length === 0) {
            errors[field] = true;
          } else if (!/^[0-9]+$/.test(traite[field])) {
            errors[field] = true;
          }
        } else {
          if (!traite[field]) {
            errors[field] = true;
          }
        }
      });
      this.$set(this.validationErrors, index, errors);
      return Object.keys(errors).length === 0;
    },
    isFieldInvalid(index, field) {
      return (
        this.validationErrors[index] && this.validationErrors[index][field]
      );
    },
    handleRibInput(index, value) {
      // Allow only numbers and ensure exactly 20 characters
      const numbersOnly = value.replace(/[^0-9]/g, "");
      const truncatedValue = numbersOnly.slice(0, 20);
      this.$emit("update-traite", index, "rib", truncatedValue);
      this.validateTraite(index);
    },
    handleNumInput(index, value) {
      // Allow only numbers
      this.$emit("update-traite", index, "num", value.replace(/[^0-9]/g, ""));
      this.validateTraite(index);
    },
  },
};
</script>

<style scoped>
.traite-section {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.traite-header {
  padding: 16px 24px 0;
}

.traite-divider {
  margin: 0;
}

.divider-text {
  font-weight: 600;
  color: #1890ff;
  font-size: 16px;
}

.traite-columns-header {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 2fr 1fr 1fr 40px;
  gap: 12px;
  padding: 16px 24px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
  font-weight: 600;
  color: #262626;
  font-size: 14px;
}

.column-header {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.column-header:first-child,
.column-header:last-child {
  justify-content: center;
}

.traite-items {
  max-height: 400px;
  overflow-y: auto;
}

.traite-item {
  display: grid;
  grid-template-columns: 60px 1fr 1fr 1fr 2fr 1fr 1fr 40px;
  gap: 12px;
  padding: 16px 24px;
  border-bottom: 1px solid #f0f0f0;
  transition: all 0.3s ease;
}

.traite-item:hover {
  background: #f6ffed;
}

.traite-item--highlighted {
  background: #fafafa;
}

.traite-item--highlighted:hover {
  background: #f6ffed;
}

.traite-index {
  display: flex;
  align-items: center;
  justify-content: center;
}

.traite-number {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #1890ff;
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 14px;
}

.traite-field {
  display: flex;
  align-items: center;
}

.traite-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.remove-btn {
  color: #ff4d4f;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.3s ease;
}

.remove-btn:hover {
  background: #fff2f0;
  color: #ff7875;
}

.traite-actions-footer {
  padding: 24px;
  text-align: center;
  border-top: 1px solid #f0f0f0;
}

.add-traite-btn {
  height: 48px;
  padding: 0 32px;
  font-size: 16px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.add-traite-btn:hover {
  border-color: #1890ff;
  color: #1890ff;
}

.radio-option {
  margin-right: 16px;
}

.radio-label {
  font-size: 13px;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .traite-columns-header,
  .traite-item {
    grid-template-columns: 50px 1fr 1fr 1fr 1fr 1fr 1fr 60px;
    gap: 8px;
    padding: 12px 16px;
  }

  .traite-columns-header {
    padding: 12px 16px;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .traite-columns-header,
  .traite-item {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
  }

  .column-header {
    justify-content: flex-start;
    text-align: left;
  }

  .traite-field {
    flex-direction: column;
    align-items: flex-start;
  }

  .traite-actions {
    justify-content: flex-start;
  }

  .traite-index {
    justify-content: flex-start;
  }
}

/* Scrollbar Styling */
.traite-items::-webkit-scrollbar {
  width: 6px;
}

.traite-items::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.traite-items::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.traite-items::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.input-error {
  border: 1px solid #ff4d4f !important;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
}
</style>
